// Password policy — the client half.
//
// The server is the authority (`security/password_policy_errors`); this exists
// so an admin sees which rules they have met *while typing*, rather than
// discovering them one rejected submit at a time. The two must state the same
// rules — if you change one, change the other.
//
// Deliberately not a regex: the rules are enumerated so the UI can render them
// individually and show which are satisfied.

export const PASSWORD_MIN_LENGTH = 15;

export interface PasswordRule {
  /** Shown in the checklist, phrased so it reads after "Must contain…". */
  readonly label: string;
  readonly test: (value: string) => boolean;
}

// Anything that is not a letter or a digit counts as a special character, so a
// passphrase with a space or a dash qualifies. Matches the server's
// `string.punctuation | {" "}`.
const SPECIAL = /[^A-Za-z0-9]/;

export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    label: `At least ${PASSWORD_MIN_LENGTH} characters`,
    test: (v) => v.length >= PASSWORD_MIN_LENGTH,
  },
  { label: "An uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { label: "A lowercase letter", test: (v) => /[a-z]/.test(v) },
  { label: "A number", test: (v) => /[0-9]/.test(v) },
  { label: "A special character", test: (v) => SPECIAL.test(v) },
];

/** True when every rule passes. */
export function passwordMeetsPolicy(value: string): boolean {
  return PASSWORD_RULES.every((r) => r.test(value));
}

/** The first unmet rule, for a single-line form error. Null when compliant. */
export function firstPasswordFailure(value: string): string | null {
  return PASSWORD_RULES.find((r) => !r.test(value))?.label ?? null;
}
